import React, { forwardRef, useEffect, useId } from "react";
import { DatePicker, TextInput } from "@jsq/ds/core";
import { DATE_PICKER_PLACEHOLDER, formatDateOnly, parseDateOnly } from "./attributeValues";

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

/**
 * DS DatePicker's built-in trigger is `TextInput`, which is not `forwardRef`.
 * react-datepicker clones that node and focuses it after a day click, which
 * warns and fails. This wrapper keeps the DS chrome and points the ref at the
 * inner `<input>`.
 */
const DatePickerInput = forwardRef<HTMLInputElement, React.ComponentProps<typeof TextInput>>(
  function DatePickerInput(props, ref) {
    return (
      <div
        style={{ display: "contents" }}
        ref={(node) => assignRef(ref, node?.querySelector("input") ?? null)}
      >
        <TextInput {...props} />
      </div>
    );
  },
);

/**
 * Calendar day picker that speaks YYYY-MM-DD to callers (the backend Date scalar)
 * and MM/DD/YYYY to the person. Shared so SpaceEditor, DocumentTypeSettings, and
 * upload don't each invent a DatePicker wiring.
 *
 * The calendar portals into a host we own (still inside the dialog). Portaling
 * to `#jsq-app` made a day click a Radix "interact outside" and unmounted the
 * form. Callers in ModalV2 still set `closeOnClickOutside={false}` so a click
 * that lands on the dimmed overlay can't discard the in-progress attribute.
 */
export function DateOnlyPicker({
  value,
  onChange,
  errorMessage,
  disabled,
}: {
  value: string;
  onChange: (isoDay: string) => void;
  errorMessage?: string;
  disabled?: boolean;
}) {
  const portalId = `date-only-picker-${useId().replace(/:/g, "")}`;
  const selected = parseDateOnly(value);
  useEffect(() => {
    if (value && selected == null) onChange("");
  }, [value, selected, onChange]);
  return (
    <>
      <div id={portalId} />
      <DatePicker
        selected={selected}
        onChange={(date: Date | null) => onChange(date ? formatDateOnly(date) : "")}
        dateFormat="MM/dd/yyyy"
        placeholderText={DATE_PICKER_PLACEHOLDER}
        autoComplete="off"
        errorMessage={errorMessage}
        disabled={disabled}
        portalId={portalId}
        customInput={<DatePickerInput icon="calendar" isFullWidth value="" />}
      />
    </>
  );
}
